import { useMemo, useState, useEffect } from "react";
import { Dropdown } from "react-bootstrap";
import DataTable, { TableColumn } from "react-data-table-component";
import Edit from "../../../assets/EditInWhite.svg";
import EditApplicationFieldDialog from "../../../components/ApplicationFields/EditApplicationFieldDialog";
import BoxComponent from "../../../components/CommonComponents/BoxComponent/BoxComponent";
import { PaginatorForRDT } from "../../../components/CommonComponents/Paginator/Paginator";
import SortIcon from "../../../components/CommonComponents/SortIcon/SortIcon";
import { tableCustomStyles } from "../../../components/CommonComponents/Table/Table";
import styles from "../../../components/pageHeader/pageHeader.module.css";
import { useGetFieldLabel } from "../../../contexts/LanguageContext";
import {
  TApplicationField,
  useApplicationFields,
  useApplicationFieldsCount,
} from "../../../hooks/application-fields.hooks";
import { useDebounce } from "../../../hooks/common";
import { formatDate } from "../../../utils/date";
import style from "../registrationFields/registrationFields.module.css";
import Styles from "./appFields.module.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import EditButton, { ViewButton } from "../../../components/CommonComponents/EditComponent/EditComponent";

type ActionType = {
  type: null | "edit";
  field?: TApplicationField;
};

const customStyles = {
  headRow: { style: { fontWeight: "600" } },
  rows: {
    style: {
      backgroundColor: "#EEEEEE",
      "&:nth-child(2n)": { backgroundColor: "#FFFFFF" },
      "&:hover": {
        backgroundColor: "#D8D6D6",
      },
    },
  },
};

export default function AppFields() {
  const limit = 10;
  const convert = useGetFieldLabel();
  const [view, setView] = useState<"table" | "card">("table");
  const [page, setPage] = useState(1);
  const [{ sortKey, sortOrder }, setSort] = useState({
    sortKey: "",
    sortOrder: 1,
  });

  const [searchQuery, setSearchQuery] = useState("");
  const [action, setAction] = useState<ActionType>({ type: null });
  const search = useDebounce(searchQuery);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  const [lastDate, setLastDate] = useState<string | null>(null);
  const { isLoading: isLoading1, data: count } = useApplicationFieldsCount({
    search,
    filters: selectedFilters.join(','),
    lastDate: lastDate ?? "",
  });

  const { isLoading: isLoading2, data } = useApplicationFields({
    page: page,
    limit: limit,
    search,
    sort: sortKey,
    sortVal: sortOrder,
    filters: selectedFilters.join(','),
    lastDate: lastDate ?? "",
  });


  const handleDropdownToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    setIsDropdownOpen(prev => !prev);
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Element;
      const filterButton = document.querySelector(`.${styles.filterIcon}`);

      if (isDropdownOpen &&
        !target.closest('.dropdown-container') &&
        target !== filterButton) {
        setIsDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDropdownOpen]);

  const onClose = () => setAction({ type: null });

  const columns = useMemo(
    (): TableColumn<TApplicationField>[] => [
      {
        name: "Sr. No.",
        // selector: (_, i) => (page - 1) * limit + (i ?? 0) + 1,
        selector: (_, i) => {
          const serialNumber = (page - 1) * limit + (i ?? 0) + 1;
          return serialNumber.toString().padStart(3, '0');
        },
        center: true,
        grow: 0.5,
      },
      {
        name: "Field Name",
        sortable: true,
        sortField: "label",
        cell: (d) => <span className="text-center">{convert(d)}</span>,
        center: true,
        grow: 2,
      },
      {
        name: "Last Published Date",
        width: "240px",
        sortable: true,
        sortField: "updatedAt",
        selector: (d) => formatDate(d.updatedAt),
        center: true,
        sortFunction: (a: any, b: any) => {
          const dateA = new Date(a.updated_date);
          const dateB = new Date(b.updated_date);
          return dateA.getTime() - dateB.getTime();
        }
      },
      {
        name: "Status",
        center: true,
        sortable: true,
        sortField: "active",
        cell: (d) => (
          <div
            className={`${d.active ? "activeButtonCss" : "inactiveButtonCss"
              // } inline-block py-1 px-4 md:px-6 rounded-full text-white text-xs shrink-0`}
              } flex justify-center items-center`}
          >
            {d?.active ? "Active" : "Inactive"}
          </div>
        ),
        grow: 1,
        width: "120px",
      },
      {
        name: 'Created By',
        selector: (row: any) => row.createdBy,
        cell: (row: any) => <span className="text-center">{row.createdBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Created On',
        selector: (row: any) => formatDate(new Date(row.createdAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified By',
        selector: (row: any) => row.modifiedBy,
        cell: (row: any) => <span className="text-center">{row.modifiedBy}</span>,
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: 'Modified On',
        selector: (row: any) => formatDate(new Date(row.updatedAt)),
        center: true,
        sortable: true,
        width: "170px",
      },
      {
        name: "Action",
        center: true,
        grow: 0.5,
        cell: (d) => (
          // <img
          //   src={Edit}
          //   alt="Edit"
          //   onClick={() => setAction({ type: "edit", field: d })}
          //   className="globalEditCss"
          // />
          <div className="flex space-x-2">
            <EditButton
              onClick={() => setAction({ type: "edit", field: d })}
            />
            <ViewButton
              disabled={true}
              onClick={function (): void {
                throw new Error("Function not implemented.");
              }}
            />
          </div>
        ),
      },
    ],
    [setAction, convert, page, limit]
  );

  const handleDateChange = (date: Date | null) => {
    if (date) {
      // Adjust for timezone issues
      const adjustedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
      const dateString = adjustedDate.toISOString().split('T')[0];
      setLastDate(dateString);
      // You might want to trigger a refetch of your data here
    } else {
      setLastDate(null);
    }
  };

  const dropdownContent = (
    <div
      className={Styles.dropdownBox}
    // style={{ marginTop: "2px", marginLeft: "-142px" }}
    >
      <ul>
        <Dropdown.Menu show className={style.DropDropMenu}>
          <div className={style.dropContent}>
            <Dropdown.Header className={style.DropTile}>
              Application fields filter
            </Dropdown.Header>
            <ul>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    checked={selectedFilters.includes("1")}
                    className={style.checkBoxDrop}
                    onChange={() => {
                      setSelectedFilters(prev =>
                        prev.includes("1")
                          ? prev.filter(f => f !== "1")
                          : [...prev, "1"]
                      );
                    }}
                  />{" "}
                  <label>Active Status</label>
                </div>
              </li>
              <li>
                <div className={style.dropdownCheck}>
                  <input
                    type="checkbox"
                    checked={selectedFilters.includes("0")}
                    className={style.checkBoxDrop}
                    onChange={() => {
                      setSelectedFilters(prev =>
                        prev.includes("0")
                          ? prev.filter(f => f !== "0")
                          : [...prev, "0"]
                      );
                    }}
                  />{" "}
                  Inactive Status
                </div>
              </li>
              <li>
                <span className="text-sm pl-2"> Last Publish Date</span>
                <DatePicker
                  selected={lastDate ? new Date(lastDate) : null}
                  onChange={handleDateChange}
                  dateFormat="dd/MM/yyyy"
                  className="border border-black"
                  isClearable
                />
              </li>
            </ul>
          </div>
        </Dropdown.Menu>
      </ul>
    </div>
  );

  useEffect(() => {
    // Trigger data refetch here
    // This depends on how your data fetching is set up
  }, [lastDate]);

  return (
    <div className="p-4 md:p-8">
      <div className="flex-wrap gap-4 md:flex-nowrap flex items-center justify-between mb-4">
        <h4 className="text-2xl font-semibold">Application Fields</h4>
        <div className="flex gap-4">
          <button
            className="p-3 bg-white border border-[#968f8f] rounded-xl flex justify-center items-center"
            onClick={() => setView((e) => (e === "card" ? "table" : "card"))}
          >
            {view === "table" ? (
              <i className="fa fa-th text-primary m-0" />
            ) : (
              <i className="fa fa-list-ul text-primary m-0" />
            )}
          </button>
          <div>
            <button
              className={`${styles.filterIcon} fa fa-filter`}
              onClick={handleDropdownToggle}
            />
            {isDropdownOpen && (
              <div className="dropdown-container">
                <div className={style.dropdown}>{dropdownContent}</div>
              </div>
            )}
          </div>
          <div className="flex rounded-xl border border-[#968f8f] bg-white items-stretch overflow-hidden">
            <input
              type="text"
              placeholder="Enter Field Name..."
              className="bg-none px-3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="p-3 shadow-md flex justify-center items-center">
              <i className="fa fa-search text-primary m-0" />
            </div>
          </div>
        </div>
      </div>

      {view === "table" ? (
        <BoxComponent>
          <DataTable
            pagination
            sortServer
            paginationServer
            className="mt-4 rounded-0"
            columns={columns}
            data={data?.data || []}
            sortIcon={<SortIcon />}
            customStyles={tableCustomStyles}
            onChangePage={(p) => setPage(p)}
            paginationTotalRows={count?.data || 0}
            paginationComponent={PaginatorForRDT}
            progressPending={isLoading1 || isLoading2}
            onSort={(col, dir) => {
              setSort({
                sortKey: col.sortField || "",
                sortOrder: dir === "asc" ? 1 : -1,
              });
            }}
          />
        </BoxComponent>
      ) : (
        <BoxComponent>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
            {(data?.data || []).map((e) => (
              <div
                className="p-4 rounded-2xl bg-white flex flex-col shadow-lg border border-gray-300"
                key={e.id}
              >
                <div className="flex justify-between items-center space-x-4 pb-3">
                  <div
                    className={`${e.active ? "activeButtonCss" : "inactiveButtonCss"
                      // } inline-block py-1 px-4 rounded-full text-white text-xs`}
                      } flex justify-center items-center`}
                  >
                    {e?.active ? "Active" : "Inactive"}
                  </div>
                  {/* <img
                    src={Edit}
                    alt="Edit"
                    onClick={() => setAction({ type: "edit", field: e })}
                    className="globalEditCss"
                  /> */}
                  <div className="flex space-x-2">
                    <EditButton
                      onClick={() => setAction({ type: "edit", field: e })}
                    />
                    <ViewButton
                      disabled={true}
                      onClick={function (): void {
                        throw new Error("Function not implemented.");
                      }}
                    />
                  </div>
                </div>
                <div className="mb-2 flex text-[12px]">
                  <p className="font-bold text-base w-1/2">Name</p>
                  <p className="text-base mx-2">:</p>
                  <p className="text-base w-2/3">{convert(e)}</p>
                </div>
                <div className="mb-2 flex text-[12px]">
                  <p className="font-bold text-base w-1/2">
                    Last Published Date
                  </p>
                  <p className="text-base mx-2">:</p>
                  <p className="text-base w-2/3">{formatDate(e.updatedAt)}</p>
                </div>
              </div>
            ))}
          </div>
          <PaginatorForRDT
            currentPage={page}
            onChangePage={setPage}
            rowCount={count?.data || 0}
            rowsPerPage={limit}
          />
        </BoxComponent>
      )}
      {action.type === "edit" && action.field && (
        <EditApplicationFieldDialog
          field={action.field}
          open
          onClose={onClose}
        />
      )}
    </div>
  );
}
